import { Card } from '@statsbomb/kitbag-components';

import { useAtomValue, useSetAtom } from 'jotai';
import { Suspense, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { unwrappedSelectedPlayerNameAtom } from '@/atoms/player/info';
import { playerIdAtom } from '@/atoms/player/player';
import {
  playerEventMarkersDescriptionsAtom,
  playerEventMarkersForMetricAtom,
} from '@/atoms/queries/player/playerEventData';
import { VideoPlayerPanelBodyBase } from '@/components/video/VideoPlayerPanel';
import { METRIC_KEY, PLAYER_ID } from '@/consts/searchKeys';
import { useSetPlayerEventDataDebouncedFilterParams } from '@/hooks/apiFilterParams/useSetPlayerEventDataDebouncedFilterParams';
import { useEntityVideoPlayerPanelHeader } from '@/hooks/useEntityVideoPlayerPanelHeader';
import { useFormatVideoEvents } from '@/hooks/useFormatVideoEvents';
import { useMetricKeyFilter } from '@/hooks/useMetricKeyFilter';
import { useVideoDataFromEventMarkers } from '@/hooks/useVideoDataFromEventMarkers';

const PlayerVideoPlayerPanelBodyWithData = () => {
  useSetPlayerEventDataDebouncedFilterParams();
  const [searchParams] = useSearchParams();
  const metricKeyParam = searchParams.get(METRIC_KEY);
  const playerIdParam = searchParams.get(PLAYER_ID);
  const {
    videoIds,
    unavailableVideosCount,
    ref,
    isLoading: isLoadingUnavailableCount,
  } = useVideoDataFromEventMarkers<HTMLSpanElement>(playerEventMarkersForMetricAtom);
  const videoEventMarkersDescriptions = useAtomValue(playerEventMarkersDescriptionsAtom);
  const videoEvents = useFormatVideoEvents(videoIds, videoEventMarkersDescriptions);

  const setPlayerId = useSetAtom(playerIdAtom);
  const { setMetricKey } = useMetricKeyFilter();

  useEffect(() => {
    if (playerIdParam && metricKeyParam) {
      setPlayerId(Number(playerIdParam));
      setMetricKey(metricKeyParam);
    }
  }, [playerIdParam, metricKeyParam]);

  return (
    <span ref={ref}>
      <VideoPlayerPanelBodyBase
        isLoading={isLoadingUnavailableCount || !playerIdParam || !metricKeyParam}
        videoEvents={videoEvents}
        unavailableVideosCount={unavailableVideosCount}
      />
    </span>
  );
};

const PlayerVideoPlayerPanelBody = () => (
  <Suspense fallback={<VideoPlayerPanelBodyBase videoEvents={[]} isLoading />}>
    <PlayerVideoPlayerPanelBodyWithData />
  </Suspense>
);

export const PlayerVideoPreview = () => {
  useEntityVideoPlayerPanelHeader(unwrappedSelectedPlayerNameAtom, PLAYER_ID);

  return (
    <Card>
      <PlayerVideoPlayerPanelBody />
    </Card>
  );
};
